import { I18nThemed } from 'interfaces/mixins';
import { lccstore } from 'lcc-state';
import timesync from 'timesync';

import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';
import has from '@dojo/framework/core/has';
import { DNode, WidgetProperties } from '@dojo/framework/core/interfaces';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { v, w } from '@dojo/framework/core/vdom';
import { Messages } from '@dojo/framework/i18n/i18n';
import Button from '@dojo/widgets/button';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import Label from '@dojo/widgets/label';
import Radio from '@dojo/widgets/radio';

import { convertToBoolean } from '@holmescorp/common';

import css from '../css/mcquestion.m.css';
import { IResult, QFResultData, QRLocator, Question, QuestionTypeProperties, Distractor } from '../interfaces';
import { setQuestion } from '../support/state';
import Correct from '../widgets/correct';
import { CountdownTimer } from '../widgets/countdown-timer';

export interface MCQuestionProperties extends QuestionTypeProperties {
	question: Question;
	quizkind: string;
	localizedBundle: { isPlaceholder: boolean; format: any | null; messages: Messages };
}

const ISSEEN = 0;
const ISCOMPLETE = 1;
const ISCORRECT = 2;

@theme(css)
export class MCQuestionBase extends I18nThemed<MCQuestionProperties> {
	private _propsInited: boolean = false;

	// Added this new private function to modify the content of the question for A11Y situations
	protected modifyQuestion(htmlText: string): string {
		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = htmlText;
		const strongTag = tempDiv.querySelector('strong');
		if (strongTag && strongTag.parentElement?.matches('div')) {
			const parentNode = strongTag.parentNode;
			const h2Tag = document.createElement('h2');
			h2Tag.appendChild(strongTag);
			parentNode?.insertBefore(h2Tag, parentNode.firstChild);
		}
		const divElements = tempDiv.querySelectorAll('div');
		const lastDiv = divElements[divElements.length - 1];
		if (lastDiv) {
			lastDiv.setAttribute('id', 'question-label');
		}
		const modifiedText = tempDiv.innerHTML;
		return modifiedText;
	}

	protected onChange(value: string): void {
		const { question, quizstore } = this.properties;
		question.distractororder.forEach((key) => {
			question.distractordata[key].chosen = false;
		});
		question.distractordata[+value].chosen = true;
		const idx = question.distractororder.indexOf(+value);
		question.flags[ISCORRECT] = idx > -1 && question.flags[idx + 4];
		question.answer = value;
		setQuestion(quizstore)({ question });
		this.invalidate();
	}

	protected onAttach(): void {
		lccstore.onChange(lccstore.path('dynls'), () => this.invalidate());
	}

	@beforeProperties()
	ensure_nls(properties: MCQuestionProperties & WidgetProperties): MCQuestionProperties & WidgetProperties {
		const { get, path } = lccstore;
		const dynls = get(path('dynls'));
		const bundle = dynls.get('quiz');
		if (!bundle) {
			properties.localizedBundle = { isPlaceholder: true, format: null, messages: {} as Messages };
		} else {
			properties.localizedBundle = this.localizeBundle(bundle);
		}
		return properties;
	}

	protected update_result(): Promise<any> {
		const { question, updater } = this.properties;
		const { icode = {}, status, cursor, answer } = question;

		question.flags[ISSEEN] = true;
		question.flags[ISCOMPLETE] = !!answer;
		// question.flags[ISCORRECT] = true;

		const data: QFResultData = {
			answer,
			icode: { ...icode, _answer: answer as any },
			starttime: question.starttime!,
			stoptime: timesync.now().toJSON(),
			isseen: true,
			ismarked: question.marked ? true : false,
			iscorrect: question.flags[ISCORRECT]
		};
		const qrlocator: QRLocator = {
			status,
			cursor,
			qrid: '',
			quiz: ''
		};
		const result: IResult = { qrlocator, data };
		return updater([result]);
	}

	protected submitted(): void {
		this.update_result();
		const { question, quizstore } = this.properties;
		question.status = 'feedback';
		setQuestion(quizstore)({ question });
		this.invalidate();
	}

	protected get_numberofquestions(): number {
		const { quizstore } = this.properties;
		const { get, path } = quizstore;
		return get(path('info')).numberofquestions;
	}

	protected render_hint(iscorrect: boolean): DNode {
		const showflag = has('use-hints') && iscorrect;
		return v('div', { key: 'hint', classes: [this.theme(css.distractorHint)] }, [
			showflag ? v('i', { classes: ['far', 'fa-arrow-alt-circle-right'] }) : null
		]);
	}

	protected render_buttons(which: { islast?: boolean; isfeedback?: boolean }): DNode[] {
		const {
			messages: {
				finish: finishText = '',
				next_question: nextText = '',
				submit: submitText = '',
				show_posttest_timer = 'true'
			}
		} = this.properties.localizedBundle;
		const { quizstore, onNextQuestion, onFinish, theme } = this.properties;
		const { islast = false, isfeedback = false } = which;
		let inFinish = false;
		console.log('ML mc-base render_buttons');
		const showTimer = convertToBoolean(show_posttest_timer);
		const timer = v('div', { key: 'timer', classes: [this.theme(css.timerblock)] }, [
			v('div', { classes: [this.theme(css.timer)] }, [w(CountdownTimer, { quizstore, countdown: '' })])
		]);

		const buttons = isfeedback
			? // buttons on feedback view
			  islast
				? v('div', { classes: [this.theme(css.primaryButton)] }, [
						w(
							Button,
							{
								theme,
								key: 'finish',
								onClick: async () => {
									if (!inFinish) {
										inFinish = true;
										await this.update_result();
										onFinish();
									}
								}
							},
							[finishText]
							// tslint:disable-next-line:indent
						)
				  ])
				: v('div', { classes: [this.theme(css.primaryButton)] }, [
						w(
							Button,
							{
								theme,
								key: 'next',
								onClick: () => {
									this.update_result();
									onNextQuestion();
								}
							},
							[
								v('span', {}, [
									nextText,
									v('i', { classes: ['fas', 'fa-arrow-right', this.theme(css.actionIcon)] })
								])
							]
							// tslint:disable-next-line:indent
						)
				  ])
			: // buttons on question view
			  v('div', { classes: [this.theme(css.primaryButton)] }, [
					w(
						Button,
						{
							theme,
							key: 'submit',
							onClick: () => {
								this.submitted();
							}
						},
						[submitText]
						// tslint:disable-next-line:indent
					)
			  ]);
		return [buttons, showTimer ? timer : null];
	}

	protected render_question(): DNode {
		const { question, theme } = this.properties;
		const { distractororder, distractordata, text, id, questiontype, flags } = question;
		const numberofquestions = this.get_numberofquestions();
		const islast = question.cursor + 1 === numberofquestions;
		const { messages } = this.properties.localizedBundle;
		const distractorLegend = messages['distractor_legend'] || '';

		const modifiedText = this.modifyQuestion(text);

		const distractors: DNode[] = distractororder.map((which, idx) => {
			const { text, chosen } = distractordata[which];
			const widgetId = `d-${which}`;
			return v(
				'div',
				{
					key: `${which}`,
					classes: [this.theme(css.distractor)]
				},
				[
					this.render_hint(flags[idx + 4]),
					w(Radio, {
						theme,
						widgetId,
						name: `mc-distractor`,
						value: `${which}`,
						checked: chosen,
						onValue: () => this.onChange(`${which}`)
					}),
					w(Label, { forId: widgetId, theme }, [
						v('div', { classes: [this.theme(css.distractorLabel)], innerHTML: text })
					])
				]
			);
		});

		return v('div', { key: id, xqa: `${questiontype} ${id}`, classes: [this.theme(css.root)] }, [
			v('div', { key: 'question-text', classes: [this.theme(css.question)], innerHTML: modifiedText }),
			v('fieldset', { key: 'distractors-fieldset', classes: [this.theme(css.distractorFieldset)] }, [
				v('legend', { classes: [this.theme(css.distractorLegend)] }, [distractorLegend]),
				v(
					'div',
					{
						key: 'distractors',
						role: 'group',
						...formatAriaProperties({ labelledby: 'question-label' }),
						classes: [this.theme(css.distractorBlock)]
					},
					distractors
				)
			]),
			v('div', { key: 'action-block', xqa: 'action block', classes: [this.theme(css.actions)] }, [
				v(
					'div',
					{ key: 'submit-action', xqa: 'action submit', classes: [this.theme(css.oneaction)] },
					this.render_buttons({ islast })
				)
			])
		]);
	}

	protected render_feedback(): DNode {
		const {
			messages: {
				correct: correctText = '',
				incorrect: incorrectText = '',
				rationale: rationaleText = '',
				for_more_info: fmiText = '',
				distractor_legend: distractorLegend = ''
			}
		} = this.properties.localizedBundle;
		const { question } = this.properties;
		const { distractororder, distractordata, flags, text, feedback, refname, id, questiontype, answer } = question;
		const numberofquestions = this.get_numberofquestions();
		const islast = question.cursor + 1 === numberofquestions;

		const modifiedText = this.modifyQuestion(text);

		if (!answer) {
			/* add 0 to the order to indicate that there was no answer */
			distractororder.push(0);
		}

		const distractors: DNode[] = distractororder.map((which, idx) => {
			const { text, chosen } =
				which > 0
					? distractordata[which]
					: {
							text: '<div>No Answer</div>',
							chosen: true
					  };
			const iscorrect = which > 0 ? flags[idx + 4] : false;
			const hasflag = iscorrect || (chosen && !iscorrect);
			const widgetId = `d-${which}`;

			return v('div', { key: `${which}`, classes: [this.theme(css.distractor)] }, [
				v('div', { key: 'hint', classes: [this.theme(css.distractorHint)] }),
				w(Radio, {
					widgetId,
					disabled: true,
					name: `mc-distractor`,
					value: `${which}`,
					checked: chosen,
					onValue: () => {
						//
					}
				}),
				w(Label, { forId: widgetId }, [
					iscorrect ? w(Correct, { key: which, iscorrect, title: correctText }) : null,
					chosen && !iscorrect ? w(Correct, { key: which, iscorrect, title: incorrectText }) : null,
					v('div', {
						classes: [this.theme(css.distractorLabel), hasflag ? this.theme(css.distractorWithFlag) : null],
						innerHTML: text
					})
				])
			]);
		});

		return v('div', { key: id, xqa: `${questiontype} ${id}`, classes: [this.theme(css.root)] }, [
			v('div', { key: 'feedback-block', classes: [this.theme(css.feedback)] }, [
				v('h2', { key: 'rationale', classes: [this.theme(css.rationale)] }, [rationaleText]),
				v('div', { innerHTML: feedback }),
				v('p', { classes: [this.theme(css.fmi)] }, [`${fmiText} ${refname}`])
			]),
			v('div', {
				key: 'question-text',
				classes: [this.theme(css.question)],
				innerHTML: modifiedText
			}),
			v('fieldset', { key: 'distractors-fieldset', classes: [this.theme(css.distractorFieldset)] }, [
				v('legend', { classes: [this.theme(css.distractorLegend)] }, [distractorLegend]),
				v(
					'div',
					{
						key: 'distractors',
						role: 'group',
						...formatAriaProperties({ labelledby: 'question-label' }),
						classes: [this.theme(css.distractorBlock)]
					},
					distractors
				)
			]),
			v('div', { key: 'action-block', xqa: 'action block', classes: [this.theme(css.actions)] }, [
				v(
					'div',
					{ key: 'submit-action', xqa: 'action submit', classes: [this.theme(css.oneaction)] },
					this.render_buttons({ islast, isfeedback: true })
				)
			])
		]);
	}

	protected render(): DNode {
		const { isPlaceholder } = this.properties.localizedBundle;
		if (isPlaceholder) {
			return null;
		}
		const { question } = this.properties;
		if (!question) {
			return null;
		}
		const { status } = question;

		return status === 'question' ? this.render_question() : this.render_feedback();
	}

	@beforeProperties()
	protected stateProperties(
		properties: MCQuestionProperties & WidgetProperties
	): MCQuestionProperties & WidgetProperties {
		const { quizstore } = properties;
		const { get, path } = quizstore;

		if (!this._propsInited) {
			const paths = [['question'], ['quiz'], ['loaded']];
			const invalidatePaths = paths.map((path: any) => quizstore.path(path.join('/')));
			const handle = quizstore.onChange(invalidatePaths, () => this.invalidate());
			this.own({
				destroy: () => {
					handle.remove();
				}
			});
			const h2 = lccstore.onChange(lccstore.path('dynls'), () => this.invalidate());
			this.own({
				destroy: () => {
					h2.remove();
				}
			});
			this._propsInited = true;
		}

		properties.quizkind = get(path('quiz')).kind;
		properties.question = get(path('question'));
		properties.loaded = get(path('loaded'));
		return properties;
	}
}

export default MCQuestionBase;
